import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { UserContext } from '../../Context/UserContext';
import CurrencySymbol from '../CurrencySymbol';
import * as XLSX from 'xlsx';
import {
  FaArrowLeft,
  FaSearch,
  FaFilter,
  FaFileExcel,
  FaPrint,
  FaSortAmountDown,
  FaSortAmountUp,
  FaUtensils,
  FaCalendarAlt,
  FaFire,
  FaBoxOpen,
  FaDollarSign,
  FaCrown,
  FaBuilding,
  FaCalendarDay,
  FaHistory
} from 'react-icons/fa';

export default function TrendingDishesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, baseUrl, getHeaders } = useContext(UserContext);

  // Initial State from Navigation or Defaults
  const initialTf = location.state?.initialTimeframe || 'Year';
  const initialDate = location.state?.selectedDashboardDate 
    ? new Date(location.state.selectedDashboardDate) 
    : new Date();

  const [activeCompany, setActiveCompany] = useState(() => {
    return location.state?.passedCompany || localStorage.getItem('active_company') || user?.company_name || user?.company || 'All';
  });
  const [activeBranch, setActiveBranch] = useState(() => {
    return location.state?.passedBranch || localStorage.getItem('active_branch') || user?.branch_name || 'All Branches';
  });

  // Filter Modes: 'preset' (Yesterday, Today, Week, Month, Year, All Time) or 'custom' (Specific Date / Range)
  const [timeframe, setTimeframe] = useState(initialTf);
  
  // Specific Date Picker state (YYYY-MM-DD)
  const todayStr = new Date().toISOString().split('T')[0];
  const [selectedSpecificDate, setSelectedSpecificDate] = useState('');
  const [dateRangeStart, setDateRangeStart] = useState('');
  const [dateRangeEnd, setDateRangeEnd] = useState('');
  const [dateMode, setDateMode] = useState('preset'); // 'preset', 'single_day', 'custom_range'

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('All');
  const [sortBy, setSortBy] = useState('qty_desc'); // qty_desc, qty_asc, rev_desc, name_asc
  const [loading, setLoading] = useState(!location.state?.passedSalesData);

  const [salesData, setSalesData] = useState(() => location.state?.passedSalesData || []);
  const [itemsData, setItemsData] = useState(() => location.state?.passedItemsData || []);
  const [availableGroups, setAvailableGroups] = useState([]);

  // Fetch / Sync Data if not passed or page refreshed
  useEffect(() => {
    if (location.state?.passedSalesData && location.state.passedSalesData.length > 0 && salesData.length > 0) {
      setLoading(false);
      return;
    }

    let isMounted = true;
    const fetchData = async () => {
      setLoading(true);
      try {
        const comp = activeCompany;
        const branch = activeBranch;
        const isAllComp = !comp || ['all', 'all companies', 'all organizations'].includes(comp.toLowerCase().trim());
        const isAllBranch = !branch || ['all', 'all branches', 'all branch'].includes(branch.toLowerCase().trim());

        let allSales = [];
        let allItems = [];

        if (isAllComp) {
          let companyList = [];
          if (user?.companies && user.companies.length > 0) {
            companyList = user.companies.map(c => typeof c === 'object' ? (c.company_name || c.company || c.name) : c).filter(Boolean);
          }
          if (companyList.length === 0) {
            try {
              const tokenH = { Authorization: `Bearer ${localStorage.getItem('token')}` };
              const cRes = await axios.get(`${baseUrl}/api/company-details`, { headers: tokenH });
              const details = cRes.data?.companyDetails || (Array.isArray(cRes.data) ? cRes.data : []);
              companyList = details.map(d => d.restaurantName || d.company_name || d.name).filter(Boolean);
            } catch (e) {
              console.warn('Could not fetch company list', e);
            }
          }

          if (companyList.length > 0) {
            await Promise.allSettled(
              companyList.map(async (cName) => {
                const h = getHeaders ? getHeaders(null, cName) : {};
                const [sRes, iRes, cboRes] = await Promise.allSettled([
                  axios.get(`${baseUrl}/api/sales`, { headers: h }),
                  axios.get(`${baseUrl}/api/items`, { headers: h }),
                  axios.get(`${baseUrl}/api/combo-offer`, { headers: h })
                ]);

                if (sRes.status === 'fulfilled' && Array.isArray(sRes.value.data)) allSales.push(...sRes.value.data);
                if (iRes.status === 'fulfilled' && Array.isArray(iRes.value.data)) allItems.push(...iRes.value.data);
                if (cboRes.status === 'fulfilled' && Array.isArray(cboRes.value.data)) {
                  const formattedCombos = cboRes.value.data.map(c => ({
                    ...c,
                    item_name: c.combo_name || c.name,
                    item_group: c.combo_category || c.category || 'Combos',
                    image: (c.images && c.images.length > 0) ? `${baseUrl}/api/combo-images/${c.images[0]}` : c.image
                  }));
                  allItems.push(...formattedCombos);
                }
              })
            );
          } else {
            const [sRes, iRes] = await Promise.allSettled([
              axios.get(`${baseUrl}/api/sales`),
              axios.get(`${baseUrl}/api/items`)
            ]);
            if (sRes.status === 'fulfilled' && Array.isArray(sRes.value.data)) allSales = sRes.value.data;
            if (iRes.status === 'fulfilled' && Array.isArray(iRes.value.data)) allItems = iRes.value.data;
          }
        } else {
          const resolvedBranch = isAllBranch ? null : branch;
          const h = getHeaders ? getHeaders(resolvedBranch, comp) : {};
          const [sRes, iRes, cboRes] = await Promise.allSettled([
            axios.get(`${baseUrl}/api/sales`, { headers: h }),
            axios.get(`${baseUrl}/api/items`, { headers: h }),
            axios.get(`${baseUrl}/api/combo-offer`, { headers: h })
          ]);

          if (sRes.status === 'fulfilled' && Array.isArray(sRes.value.data)) allSales = sRes.value.data;
          if (iRes.status === 'fulfilled' && Array.isArray(iRes.value.data)) allItems = iRes.value.data;
          if (cboRes.status === 'fulfilled' && Array.isArray(cboRes.value.data)) {
            const formattedCombos = cboRes.value.data.map(c => ({
              ...c,
              item_name: c.combo_name || c.name,
              item_group: c.combo_category || c.category || 'Combos',
              image: (c.images && c.images.length > 0) ? `${baseUrl}/api/combo-images/${c.images[0]}` : c.image
            }));
            allItems.push(...formattedCombos);
          }
        }

        if (isMounted) {
          setSalesData(allSales);
          setItemsData(allItems);
        }
      } catch (err) {
        console.error('Error fetching trending dishes data:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();
    return () => { isMounted = false; };
  }, [activeCompany, activeBranch, baseUrl]);

  // Aggregate Sales Data by Item based on selected filters (Preset Timeframe or Exact Date / Range)
  const { allDishes, totalQtySold, totalRevenueAll, activeFilterLabel } = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);
    const endOfYesterday = new Date(startOfToday);
    endOfYesterday.setMilliseconds(endOfYesterday.getMilliseconds() - 1);

    const sd = new Date();
    const day = sd.getDay();
    const diff = sd.getDate() - day + (day === 0 ? -6 : 1);
    const startOfWeek = new Date(sd.getFullYear(), sd.getMonth(), diff);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    const startOfMonth = new Date(sd.getFullYear(), sd.getMonth(), 1);
    const endOfMonth = new Date(sd.getFullYear(), sd.getMonth() + 1, 0, 23, 59, 59, 999);

    const startOfYear = new Date(sd.getFullYear(), 0, 1);
    const endOfYear = new Date(sd.getFullYear(), 11, 31, 23, 59, 59, 999);

    const parseSaleDate = (sale) => {
      const raw = sale.date || sale.created_at || sale.posting_date || '';
      if (!raw) return null;
      if (raw.includes('T')) return new Date(raw);
      if (raw.includes('-')) {
        const p = raw.split('-');
        if (p[0].length === 4) return new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
        return new Date(Number(p[2]), Number(p[1]) - 1, Number(p[0]));
      }
      return new Date(raw);
    };

    const getFormattedSaleDateStr = (sDate) => {
      if (!sDate || isNaN(sDate.getTime())) return '';
      const y = sDate.getFullYear();
      const m = String(sDate.getMonth() + 1).padStart(2, '0');
      const d = String(sDate.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };

    let filterLabel = timeframe;

    const inRange = (sDate) => {
      if (!sDate || isNaN(sDate.getTime())) return false;

      // Single Day Filter
      if (dateMode === 'single_day' && selectedSpecificDate) {
        const sStr = getFormattedSaleDateStr(sDate);
        return sStr === selectedSpecificDate;
      }

      // Custom Range Filter
      if (dateMode === 'custom_range') {
        if (dateRangeStart && dateRangeEnd) {
          const start = new Date(dateRangeStart);
          start.setHours(0, 0, 0, 0);
          const end = new Date(dateRangeEnd);
          end.setHours(23, 59, 59, 999);
          return sDate >= start && sDate <= end;
        } else if (dateRangeStart) {
          const start = new Date(dateRangeStart);
          start.setHours(0, 0, 0, 0);
          return sDate >= start;
        } else if (dateRangeEnd) {
          const end = new Date(dateRangeEnd);
          end.setHours(23, 59, 59, 999);
          return sDate <= end;
        }
        return true;
      }

      // Presets
      if (timeframe === 'Today') return sDate >= startOfToday;
      if (timeframe === 'Yesterday') return sDate >= startOfYesterday && sDate <= endOfYesterday;
      if (timeframe === 'Week') return sDate >= startOfWeek && sDate <= endOfWeek;
      if (timeframe === 'Month') return sDate >= startOfMonth && sDate <= endOfMonth;
      if (timeframe === 'Year') return sDate >= startOfYear && sDate <= endOfYear;
      if (timeframe === 'All Time') return true;
      return true;
    };

    if (dateMode === 'single_day' && selectedSpecificDate) {
      filterLabel = `Date: ${selectedSpecificDate}`;
    } else if (dateMode === 'custom_range' && (dateRangeStart || dateRangeEnd)) {
      filterLabel = `Range: ${dateRangeStart || 'Start'} to ${dateRangeEnd || 'End'}`;
    }

    // Build Master Items Map
    const itemMaster = new Map();
    const groupsSet = new Set();

    (itemsData || []).forEach(it => {
      const name = (it.item_name || it.name || it.addon_name || it.combo_name || '').trim();
      const grp = (it.item_group || it.category || '').trim();
      const price = parseFloat(it.price || it.rate || it.standard_rate || 0);
      if (grp && !['all', 'global'].includes(grp.toLowerCase())) {
        groupsSet.add(grp);
      }
      if (name) {
        itemMaster.set(name.toLowerCase(), {
          imageUrl: it.addon_image || it.image || it.item_image || it.imageUrl || '',
          group: grp,
          price
        });
      }
      if (it.addonImages && typeof it.addonImages === 'object') {
        Object.entries(it.addonImages).forEach(([addonName, addonUrl]) => {
          if (addonName) {
            groupsSet.add('Addons');
            const key = addonName.trim().toLowerCase();
            if (!itemMaster.has(key)) {
              itemMaster.set(key, { imageUrl: addonUrl, group: 'Addons', price: 0 });
            }
          }
        });
      }
    });

    const getItemGroup = (saleItemName, saleItemGroup) => {
      const master = itemMaster.get((saleItemName || '').toLowerCase());
      return master?.group || saleItemGroup || '';
    };

    const getItemImage = (saleItemName) => {
      const master = itemMaster.get((saleItemName || '').toLowerCase());
      return master?.imageUrl || '';
    };

    const getItemPrice = (saleItemName, itm) => {
      const p = parseFloat(itm.price || itm.rate || itm.standard_rate || itm.basePrice || itm.amount || 0);
      if (p > 0) return p;
      const master = itemMaster.get((saleItemName || '').toLowerCase());
      return master?.price || 0;
    };

    const classifyGroup = (groupName) => {
      const g = (groupName || '').toLowerCase();
      if (/drink|bev|juice|latte|coffee|tea|mocktail|cocktail|shake|soda|water/.test(g)) return 'Drinks';
      if (/food|burger|pasta|pizza|chicken|rice|noodle|starter|main|grill|wrap|salad|soup|sandwich|snack|dessert|sweet/.test(g)) return 'Food';
      return 'Others';
    };

    let filteredSales = salesData.filter(sale => inRange(parseSaleDate(sale)));
    if (filteredSales.length === 0 && salesData.length > 0 && dateMode === 'preset' && (timeframe === 'Year' || timeframe === 'All Time')) {
      filteredSales = salesData;
    }

    const itemMap = new Map();
    let totalQty = 0;
    let totalRev = 0;

    filteredSales.forEach(sale => {
      let parsedItems = sale.items || [];
      if (typeof parsedItems === 'string') {
        try { parsedItems = JSON.parse(parsedItems); } catch (e) { parsedItems = []; }
      }
      if (!Array.isArray(parsedItems)) parsedItems = [];

      parsedItems.forEach(item => {
        const processItemData = (itm, parentQty = 1, defaultGroup = 'Uncategorized') => {
          const qtyField = parseInt(itm.quantity || itm.qty || itm.addon_quantity || itm.combo_quantity) || 1;
          const q = qtyField * (parentQty === 1 && qtyField ? 1 : parentQty);

          let name = (itm.item_name || itm.name || itm.name1 || itm.addon_name || itm.modifier_name || itm.modifier || itm.combo_item_name || itm.combo_name || itm.title || itm.variant_name || 'Item').trim();

          if (name.toLowerCase().startsWith('+ addon:')) name = name.substring(8).trim();
          else if (name.toLowerCase().startsWith('addon:')) name = name.substring(6).trim();
          else if (name.toLowerCase().startsWith('+ combo:')) name = name.substring(8).trim();
          else if (name.toLowerCase().startsWith('combo:')) name = name.substring(6).trim();

          let cleanName = name.replace(/\s*\([a-zA-Z0-9:\s]+\)/g, '').replace(/\s*x[0-9]+$/, '').trim();
          const testGroup = getItemGroup(cleanName, '');
          const testImage = getItemImage(cleanName);
          if (testImage !== '' || (testGroup && testGroup !== 'Uncategorized')) {
            name = cleanName;
          }

          let rawGroup = getItemGroup(name, itm.item_group || itm.category || '');
          if (!rawGroup || rawGroup === 'Uncategorized') rawGroup = defaultGroup;
          groupsSet.add(rawGroup);

          const classifiedLabel = classifyGroup(rawGroup);
          let inlineImage = itm.addon_image || itm.combo_image || itm.image || '';
          if (inlineImage && inlineImage.startsWith('/')) inlineImage = `${baseUrl}${inlineImage}`;

          const unitPrice = getItemPrice(name, itm);
          const lineRevenue = unitPrice * q;

          if (!itemMap.has(name)) {
            itemMap.set(name, {
              name,
              quantity: 0,
              realGroup: rawGroup,
              classifiedLabel,
              imageUrl: inlineImage || getItemImage(name),
              unitPrice: unitPrice,
              totalRevenue: 0
            });
          }
          const entry = itemMap.get(name);
          entry.quantity += q;
          entry.totalRevenue += lineRevenue;
          if (unitPrice > 0 && entry.unitPrice === 0) {
            entry.unitPrice = unitPrice;
          }

          totalQty += q;
          totalRev += lineRevenue;
        };

        const itemQ = parseInt(item.quantity) || 1;
        processItemData(item, 1, 'Uncategorized');

        // Addons
        let itemAddons = item.addons || item.modifiers || [];
        if (typeof itemAddons === 'string') {
          try { itemAddons = JSON.parse(itemAddons); } catch (e) { itemAddons = []; }
        }
        if (Array.isArray(itemAddons)) {
          itemAddons.forEach(addon => {
            if (typeof addon === 'object' && addon !== null) processItemData(addon, itemQ, 'Addons');
            else processItemData({ name: String(addon) }, itemQ, 'Addons');
          });
        }

        // Combos
        let itemCombos = item.selectedCombos || item.combo_items || item.combos || [];
        if (typeof itemCombos === 'string') {
          try { itemCombos = JSON.parse(itemCombos); } catch (e) { itemCombos = []; }
        }
        if (Array.isArray(itemCombos)) {
          itemCombos.forEach(combo => {
            if (typeof combo === 'object' && combo !== null) processItemData(combo, itemQ, 'Combos');
            else processItemData({ name: String(combo) }, itemQ, 'Combos');
          });
        }
      });
    });

    setAvailableGroups(['All', ...Array.from(groupsSet).sort()]);

    const dishesArray = Array.from(itemMap.values()).sort((a, b) => b.quantity - a.quantity);
    return {
      allDishes: dishesArray,
      totalQtySold: totalQty,
      totalRevenueAll: totalRev,
      activeFilterLabel: filterLabel
    };
  }, [salesData, itemsData, timeframe, dateMode, selectedSpecificDate, dateRangeStart, dateRangeEnd, baseUrl]);

  // Filter & Sort
  const processedDishes = useMemo(() => {
    let result = [...allDishes];

    // Filter by group
    if (selectedGroup !== 'All') {
      result = result.filter(d => 
        (d.realGroup && d.realGroup.toLowerCase() === selectedGroup.toLowerCase()) || 
        (d.classifiedLabel && d.classifiedLabel.toLowerCase() === selectedGroup.toLowerCase())
      );
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(d => 
        d.name?.toLowerCase().includes(q) || 
        d.realGroup?.toLowerCase().includes(q)
      );
    }

    // Sort
    result.sort((a, b) => {
      if (sortBy === 'qty_desc') return b.quantity - a.quantity;
      if (sortBy === 'qty_asc') return a.quantity - b.quantity;
      if (sortBy === 'rev_desc') return b.totalRevenue - a.totalRevenue;
      if (sortBy === 'name_asc') return a.name.localeCompare(b.name);
      return b.quantity - a.quantity;
    });

    return result;
  }, [allDishes, selectedGroup, searchQuery, sortBy]);

  const maxSoldQty = useMemo(() => {
    return allDishes.length > 0 ? Math.max(...allDishes.map(d => d.quantity)) : 1;
  }, [allDishes]);

  const topDish = useMemo(() => {
    if (allDishes.length === 0) return null;
    return allDishes[0];
  }, [allDishes]);

  // Export to Excel
  const handleExportExcel = () => {
    if (processedDishes.length === 0) return;
    const exportData = processedDishes.map((dish, idx) => ({
      Rank: idx + 1,
      'Item Name': dish.name,
      Category: dish.realGroup || dish.classifiedLabel,
      'Orders / Quantity': dish.quantity,
      'Approx Unit Price': dish.unitPrice ? dish.unitPrice.toFixed(2) : '0.00',
      'Total Revenue': dish.totalRevenue ? dish.totalRevenue.toFixed(2) : '0.00',
      'Volume Share %': totalQtySold > 0 ? ((dish.quantity / totalQtySold) * 100).toFixed(1) + '%' : '0%'
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Trending Dishes');
    XLSX.writeFile(wb, `Trending_Dishes_${activeFilterLabel.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handlePrint = () => {
    window.print();
  };

  // Switch to specific date
  const handleSelectSpecificDate = (e) => {
    const val = e.target.value;
    setSelectedSpecificDate(val);
    if (val) {
      setDateMode('single_day');
    }
  };

  return (
    <div className="trending-page-container" style={{
      minHeight: '100vh',
      backgroundColor: 'var(--ap-bg, #f4f6fa)',
      color: 'var(--ap-text-primary, #1e293b)',
      padding: '30px',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
    }}>
      <style>{`
        @media print {
          body { background: #fff !important; }
          .no-print { display: none !important; }
          .trending-page-container { padding: 0 !important; }
        }
        .trending-kpi-card {
          background: #ffffff;
          border-radius: 18px;
          padding: 20px 24px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 4px 16px rgba(0,0,0,0.03);
          display: flex;
          align-items: center;
          gap: 18px;
          transition: transform 0.2s, box-shadow 0.2s;
        }
        .trending-kpi-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 25px rgba(0,0,0,0.07);
        }
        .trending-table th {
          background-color: #f8fafc;
          color: #64748b;
          font-weight: 700;
          font-size: 13px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          padding: 16px 20px;
          border-bottom: 2px solid #e2e8f0;
        }
        .trending-table td {
          padding: 16px 20px;
          vertical-align: middle;
          border-bottom: 1px solid #edf2f7;
          font-size: 14px;
        }
        .trending-table tr:hover {
          background-color: #f8fafc;
        }
        .filter-mode-tab {
          padding: 8px 16px;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
          border: 1px solid transparent;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .filter-mode-tab.active {
          background: #604BE8;
          color: #fff;
          box-shadow: 0 4px 12px rgba(96,75,232,0.3);
        }
        .filter-mode-tab:not(.active) {
          background: #f1f5f9;
          color: #64748b;
        }
        .timeframe-pill-btn {
          padding: 7px 15px;
          border-radius: 10px;
          border: 1px solid #e2e8f0;
          background: #ffffff;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          color: #64748b;
          transition: all 0.2s;
        }
        .timeframe-pill-btn.active {
          background: #604BE8;
          color: #ffffff;
          border-color: #604BE8;
          box-shadow: 0 4px 12px rgba(96,75,232,0.3);
        }
      `}</style>

      {/* Header Toolbar */}
      <div className="no-print" style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '28px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => navigate('/admin')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 18px',
              backgroundColor: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              fontWeight: '700',
              color: '#334155',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              transition: 'all 0.2s'
            }}
            onMouseOver={e => e.currentTarget.style.backgroundColor = '#f1f5f9'}
            onMouseOut={e => e.currentTarget.style.backgroundColor = '#ffffff'}
          >
            <FaArrowLeft /> Back to Dashboard
          </button>
          <div>
            <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '800', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <FaFire style={{ color: '#FF5722' }} /> Trending Dishes & Best Sellers
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
              Sales performance & most ordered dishes for <strong style={{ color: '#604BE8' }}>{activeFilterLabel}</strong>
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button
            onClick={handleExportExcel}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 18px',
              backgroundColor: '#10b981',
              color: '#fff',
              border: 'none',
              borderRadius: '12px',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(16,185,129,0.25)'
            }}
          >
            <FaFileExcel /> Export Excel
          </button>
          <button
            onClick={handlePrint}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 18px',
              backgroundColor: '#ffffff',
              color: '#334155',
              border: '1px solid #cbd5e1',
              borderRadius: '12px',
              fontWeight: '700',
              cursor: 'pointer'
            }}
          >
            <FaPrint /> Print
          </button>
        </div>
      </div>

      {/* KPI Highlights */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '20px',
        marginBottom: '28px'
      }}>
        <div className="trending-kpi-card">
          <div style={{ width: '48px', height: '48px', borderRadius: '14px', backgroundColor: '#EDE9FE', color: '#604BE8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>
            <FaCrown />
          </div>
          <div>
            <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase' }}>#1 Top Seller</div>
            <div style={{ fontSize: '18px', fontWeight: '800', color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '160px' }}>
              {topDish ? topDish.name : '—'}
            </div>
            <div style={{ fontSize: '12px', color: '#10b981', fontWeight: '700' }}>
              {topDish ? `${topDish.quantity} Orders` : '0'}
            </div>
          </div>
        </div>

        <div className="trending-kpi-card">
          <div style={{ width: '48px', height: '48px', borderRadius: '14px', backgroundColor: '#FEF3C7', color: '#D97706', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>
            <FaBoxOpen />
          </div>
          <div>
            <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase' }}>Total Sold Items</div>
            <div style={{ fontSize: '22px', fontWeight: '800', color: '#0f172a' }}>
              {totalQtySold.toLocaleString()}
            </div>
            <div style={{ fontSize: '12px', color: '#64748b' }}>ordered in this period</div>
          </div>
        </div>

        <div className="trending-kpi-card">
          <div style={{ width: '48px', height: '48px', borderRadius: '14px', backgroundColor: '#E0E7FF', color: '#4F46E5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>
            <FaUtensils />
          </div>
          <div>
            <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase' }}>Unique Dishes Sold</div>
            <div style={{ fontSize: '22px', fontWeight: '800', color: '#0f172a' }}>
              {allDishes.length}
            </div>
            <div style={{ fontSize: '12px', color: '#64748b' }}>varieties ordered</div>
          </div>
        </div>

        <div className="trending-kpi-card">
          <div style={{ width: '48px', height: '48px', borderRadius: '14px', backgroundColor: '#DCFCE7', color: '#16A34A', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>
            <FaDollarSign />
          </div>
          <div>
            <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase' }}>Total Est. Revenue</div>
            <div style={{ fontSize: '22px', fontWeight: '800', color: '#0f172a' }}>
              <CurrencySymbol />{totalRevenueAll.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div style={{ fontSize: '12px', color: '#64748b' }}>volume value</div>
          </div>
        </div>
      </div>

      {/* Advanced Filter Box */}
      <div className="no-print" style={{
        backgroundColor: '#ffffff',
        borderRadius: '20px',
        padding: '22px 26px',
        marginBottom: '24px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 16px rgba(0,0,0,0.02)',
        display: 'flex',
        flexDirection: 'column',
        gap: '18px'
      }}>
        {/* Row 1: Filter Mode Toggle */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '13px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Filter By:
            </span>
            <button
              className={`filter-mode-tab ${dateMode === 'preset' ? 'active' : ''}`}
              onClick={() => { setDateMode('preset'); }}
            >
              <FaCalendarAlt /> Timeframe Presets
            </button>
            <button
              className={`filter-mode-tab ${dateMode === 'single_day' ? 'active' : ''}`}
              onClick={() => {
                setDateMode('single_day');
                if (!selectedSpecificDate) setSelectedSpecificDate(todayStr);
              }}
            >
              <FaCalendarDay /> Specific Day
            </button>
            <button
              className={`filter-mode-tab ${dateMode === 'custom_range' ? 'active' : ''}`}
              onClick={() => {
                setDateMode('custom_range');
                if (!dateRangeStart) setDateRangeStart(todayStr);
                if (!dateRangeEnd) setDateRangeEnd(todayStr);
              }}
            >
              <FaHistory /> Date Range
            </button>
          </div>

          {/* Sort By Dropdown */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '13px', fontWeight: '600', color: '#64748b' }}>Sort By:</span>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              style={{
                padding: '9px 14px',
                borderRadius: '10px',
                border: '1px solid #cbd5e1',
                fontSize: '13px',
                fontWeight: '600',
                color: '#334155',
                outline: 'none',
                backgroundColor: '#f8fafc',
                cursor: 'pointer'
              }}
            >
              <option value="qty_desc">🔥 Most Sold (High to Low)</option>
              <option value="qty_asc">Least Sold (Low to High)</option>
              <option value="rev_desc">💰 Highest Revenue</option>
              <option value="name_asc">🔤 Item Name (A-Z)</option>
            </select>
          </div>
        </div>

        {/* Row 2: Date Selector Details based on active mode */}
        <div style={{
          backgroundColor: '#f8fafc',
          padding: '14px 18px',
          borderRadius: '14px',
          border: '1px solid #edf2f7',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          {dateMode === 'preset' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#64748b' }}>Select Period:</span>
              {['Yesterday', 'Today', 'Week', 'Month', 'Year', 'All Time'].map(tf => (
                <button
                  key={tf}
                  className={`timeframe-pill-btn ${timeframe === tf ? 'active' : ''}`}
                  onClick={() => setTimeframe(tf)}
                >
                  {tf}
                </button>
              ))}
            </div>
          )}

          {dateMode === 'single_day' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '13px', fontWeight: '700', color: '#334155' }}>
                Select Exact Date to view most sold dishes:
              </span>
              <input
                type="date"
                value={selectedSpecificDate}
                onChange={handleSelectSpecificDate}
                style={{
                  padding: '8px 14px',
                  borderRadius: '10px',
                  border: '1.5px solid #604BE8',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#1e293b',
                  backgroundColor: '#ffffff',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              />
              <button
                onClick={() => setSelectedSpecificDate(todayStr)}
                style={{
                  padding: '7px 12px',
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #cbd5e1',
                  fontSize: '12px',
                  fontWeight: '700',
                  color: '#604BE8',
                  cursor: 'pointer'
                }}
              >
                Today
              </button>
            </div>
          )}

          {dateMode === 'custom_range' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#64748b' }}>From:</span>
              <input
                type="date"
                value={dateRangeStart}
                onChange={e => setDateRangeStart(e.target.value)}
                style={{
                  padding: '8px 14px',
                  borderRadius: '10px',
                  border: '1px solid #cbd5e1',
                  fontSize: '13px',
                  fontWeight: '600',
                  backgroundColor: '#fff',
                  outline: 'none'
                }}
              />
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#64748b' }}>To:</span>
              <input
                type="date"
                value={dateRangeEnd}
                onChange={e => setDateRangeEnd(e.target.value)}
                style={{
                  padding: '8px 14px',
                  borderRadius: '10px',
                  border: '1px solid #cbd5e1',
                  fontSize: '13px',
                  fontWeight: '600',
                  backgroundColor: '#fff',
                  outline: 'none'
                }}
              />
              {(dateRangeStart || dateRangeEnd) && (
                <button
                  onClick={() => { setDateRangeStart(''); setDateRangeEnd(''); }}
                  style={{
                    padding: '7px 12px',
                    borderRadius: '8px',
                    backgroundColor: '#fee2e2',
                    border: 'none',
                    color: '#ef4444',
                    fontSize: '12px',
                    fontWeight: '700',
                    cursor: 'pointer'
                  }}
                >
                  Clear Range
                </button>
              )}
            </div>
          )}
        </div>

        {/* Row 3: Search Box & Category Filter */}
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Search Box */}
          <div style={{
            position: 'relative',
            flex: '1 1 300px',
            minWidth: '240px'
          }}>
            <FaSearch style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input
              type="text"
              placeholder="Search by dish name or category..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px 12px 44px',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                fontSize: '14px',
                outline: 'none',
                backgroundColor: '#f8fafc',
                boxSizing: 'border-box'
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                ✕
              </button>
            )}
          </div>

          {/* Category Dropdown */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaFilter style={{ color: '#64748b' }} />
            <select
              value={selectedGroup}
              onChange={e => setSelectedGroup(e.target.value)}
              style={{
                padding: '11px 16px',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                fontSize: '14px',
                fontWeight: '600',
                color: '#334155',
                outline: 'none',
                backgroundColor: '#f8fafc',
                cursor: 'pointer'
              }}
            >
              {availableGroups.map(g => (
                <option key={g} value={g}>{g === 'All' ? 'All Categories' : g}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Main Table View */}
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '24px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 8px 30px rgba(0,0,0,0.04)',
        overflow: 'hidden'
      }}>
        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center', color: '#64748b' }}>
            <div style={{ fontSize: '18px', fontWeight: '700', marginBottom: '8px' }}>Loading Trending Dishes...</div>
            <div style={{ fontSize: '13px' }}>Fetching transactions for {activeFilterLabel}</div>
          </div>
        ) : processedDishes.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center', color: '#64748b' }}>
            <div style={{ fontSize: '42px', marginBottom: '12px' }}>🍽️</div>
            <div style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '6px' }}>No Trending Dishes Found</div>
            <div style={{ fontSize: '14px', color: '#64748b' }}>
              There are no orders matching <strong style={{ color: '#604BE8' }}>{activeFilterLabel}</strong>. Try selecting another date or period.
            </div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="trending-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr>
                  <th style={{ width: '80px', textAlign: 'center' }}>Rank</th>
                  <th>Dish / Item Details</th>
                  <th>Category</th>
                  <th style={{ width: '220px' }}>Sales Volume & Share</th>
                  <th style={{ textAlign: 'right' }}>Total Orders</th>
                  <th style={{ textAlign: 'right' }}>Unit Price</th>
                  <th style={{ textAlign: 'right' }}>Estimated Revenue</th>
                </tr>
              </thead>
              <tbody>
                {processedDishes.map((dish, idx) => {
                  const rankEmojis = ['🥇', '🥈', '🥉'];
                  const isTop3 = idx < 3;
                  const volumePct = maxSoldQty > 0 ? (dish.quantity / maxSoldQty) * 100 : 0;
                  const totalSharePct = totalQtySold > 0 ? ((dish.quantity / totalQtySold) * 100).toFixed(1) : '0';

                  const badgeColors = ['#604BE8', '#FF9F04', '#06D6A0', '#EF4444', '#3B82F6', '#8B5CF6'];
                  const catBg = badgeColors[idx % badgeColors.length];

                  return (
                    <tr key={dish.name + idx}>
                      {/* Rank Column */}
                      <td style={{ textAlign: 'center' }}>
                        {isTop3 ? (
                          <div style={{
                            fontSize: '22px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '38px',
                            height: '38px',
                            borderRadius: '50%',
                            backgroundColor: idx === 0 ? '#FEF3C7' : idx === 1 ? '#F1F5F9' : '#FFEDD5',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.06)'
                          }}>
                            {rankEmojis[idx]}
                          </div>
                        ) : (
                          <span style={{
                            display: 'inline-block',
                            padding: '4px 10px',
                            backgroundColor: '#f1f5f9',
                            borderRadius: '10px',
                            fontSize: '12px',
                            fontWeight: '800',
                            color: '#64748b'
                          }}>
                            #{idx + 1}
                          </span>
                        )}
                      </td>

                      {/* Dish Image & Name */}
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                          <div style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '12px',
                            backgroundColor: catBg + '15',
                            border: `1.5px solid ${isTop3 ? catBg : '#e2e8f0'}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            flexShrink: 0
                          }}>
                            {dish.imageUrl ? (
                              <img
                                src={dish.imageUrl}
                                alt={dish.name}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onError={e => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '🍽️'; }}
                              />
                            ) : (
                              <span style={{ fontSize: '22px' }}>🍽️</span>
                            )}
                          </div>
                          <div>
                            <div style={{ fontWeight: '700', fontSize: '15px', color: '#0f172a' }}>
                              {dish.name}
                            </div>
                            <div style={{ fontSize: '12px', color: '#64748b' }}>
                              {totalSharePct}% of total restaurant sales
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Category Badge */}
                      <td>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '8px',
                          fontSize: '11px',
                          fontWeight: '800',
                          backgroundColor: catBg + '20',
                          color: catBg,
                          display: 'inline-block'
                        }}>
                          {dish.realGroup || dish.classifiedLabel}
                        </span>
                      </td>

                      {/* Progress Visual Bar */}
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{
                            flex: 1,
                            height: '8px',
                            backgroundColor: '#e2e8f0',
                            borderRadius: '10px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${volumePct}%`,
                              height: '100%',
                              backgroundColor: idx === 0 ? '#604BE8' : idx === 1 ? '#FF9F04' : '#10B981',
                              borderRadius: '10px'
                            }} />
                          </div>
                          <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', width: '38px' }}>
                            {Math.round(volumePct)}%
                          </span>
                        </div>
                      </td>

                      {/* Quantity / Orders */}
                      <td style={{ textAlign: 'right' }}>
                        <div style={{
                          fontSize: '16px',
                          fontWeight: '800',
                          color: idx === 0 ? '#604BE8' : '#0f172a'
                        }}>
                          {dish.quantity.toLocaleString()}
                        </div>
                        <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600' }}>orders</div>
                      </td>

                      {/* Unit Price */}
                      <td style={{ textAlign: 'right', fontWeight: '600', color: '#475569' }}>
                        {dish.unitPrice > 0 ? (
                          <span><CurrencySymbol />{dish.unitPrice.toFixed(2)}</span>
                        ) : (
                          <span style={{ color: '#94a3b8' }}>—</span>
                        )}
                      </td>

                      {/* Total Revenue */}
                      <td style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '15px', fontWeight: '800', color: '#059669' }}>
                          <CurrencySymbol />{dish.totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Table Footer */}
        {!loading && processedDishes.length > 0 && (
          <div style={{
            padding: '16px 24px',
            backgroundColor: '#f8fafc',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '13px',
            color: '#64748b',
            fontWeight: '600'
          }}>
            <span>Showing {processedDishes.length} of {allDishes.length} items sold in {activeFilterLabel}</span>
            <span>Total Sales Volume: <strong style={{ color: '#0f172a' }}>{totalQtySold} Orders</strong></span>
          </div>
        )}
      </div>
    </div>
  );
}
